// 参考 https://github.com/vueuse/vueuse/blob/main/packages/core/createTemplatePromise/index.ts

import type {
  DefineComponent,
  Ref,
  TransitionProps,
  VNode,
  SetupContext,
} from 'vue'
import {
  defineComponent,
  ref,
  shallowReactive,
  Transition,
  Fragment,
  h,
  createApp,
  getCurrentInstance,
  render as vRender,
  markRaw,
  withDirectives,
  vShow,
  nextTick,
} from 'vue'

export interface TemplatePromiseOptions {
  // 过渡
  transition?: TransitionProps
  // 是否销毁
  destroy?: boolean
  //   自定义渲染
  render?: (props, ctx: SetupContext) => VNode
  // class 名
  class?: string
}
export type TemplatePromise<
  Return,
  Args extends any[] = [],
> = DefineComponent<object> & {
  //   new (): {
  //     $slots: {
  //       default: (_: TemplatePromiseProps<Return, Args>) => any
  //     }
  //   }
} & {
  open: (...args: Args) => Promise<Return>
}
export interface TemplatePromiseProps<Return, Args extends any[] = []> {
  promise: Promise<Return> | undefined
  resolve: (v: Return) => void
  reject: (v: any) => void
  args: Args
  options: TemplatePromiseOptions
  isResolving: boolean
  isVisible: boolean
  key: number
}
export const useTemplatePromise = <Return, Args extends any[] = []>(
  options: TemplatePromiseOptions = {},
): TemplatePromise<Return, Args> => {
  const {
    destroy = false,
    render,
    transition = {
      name: 'fade',
      appear: true,
    },
  } = options
  const instances = ref([]) as Ref<TemplatePromiseProps<Return, Args>[]>
  let index = 0

  const create = (...args: Args) => {
    const key = index++
    const props = shallowReactive<TemplatePromiseProps<Return, Args>>({
      key,
      args,
      promise: undefined,
      resolve: () => {},
      reject: () => {},
      isResolving: false,
      isVisible: true,
      options,
    })
    instances.value.push(props)
    props.promise = new Promise<Return>((_resolve, _reject) => {
      props.resolve = (v) => {
        props.isResolving = true
        return _resolve(v)
      }
      props.reject = _reject
    }).finally(() => {
      if (destroy) {
        props.promise = undefined
        const index = instances.value.indexOf(props)
        if (index !== -1) instances.value.splice(index, 1)
      } else {
        props.isVisible = false
        // const wrapper = document.querySelector(
        //   '.ep-template-promise',
        // ) as HTMLElement
        // if (wrapper.style['zIndex']) {
        //   return
        // }
        // const dialog = wrapper?.querySelector('.el-modal-dialog') as HTMLElement
        // const zIndex = dialog ? dialog.style['z-index'] : ''
        // if (zIndex && wrapper) {
        //   wrapper.style.zIndex = zIndex
        // }
      }
    })
    return props.promise
  }
  const open = (...args: Args) => {
    instances.value = []
    const com = create(...args)
    nextTick(() => {
      const wrapper = document.querySelector(
        '.ep-template-promise.is-init',
      ) as HTMLElement
      if (wrapper) {
        const dialog = wrapper?.querySelector('.el-modal-dialog') as HTMLElement
        const zIndex = dialog ? dialog.style['z-index'] : ''
        if (zIndex && wrapper) {
          wrapper.style.zIndex = zIndex
        }
        // 删除is-init的class
        wrapper.classList.remove('is-init')
      }
    })
    return com
  }
  const component = defineComponent((_, ctx) => {
    const { slots } = ctx
    const renderList = () =>
      instances.value.map((props) => {
        return withDirectives(
          h(
            'div',
            {
              // key: props.key,
              class: `ep-template-promise is-init ${options.class || ''}`,
            },
            render ? render(props, ctx) : slots.default?.(props),
          ),
          [[vShow, props.isVisible]],
        )
      })
    if (transition)
      return () => <Transition {...transition}>{renderList()}</Transition>

    return renderList
  })
  // @ts-expect-error There's a breaking type change in Vue 3.3 <https://github.com/vuejs/core/pull/7963>
  component.open = open
  return markRaw(component as TemplatePromise<Return, Args>)
}
